import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { APIError } from "api/utils"
import { OpenNewWindow } from "iconoir-react"
import { useRef, useState } from "react"

const MAX_SIZE = 25 * 1024 * 1024

const errorMessage = (e: unknown) => {
  if (!(e instanceof APIError))
    return "Something went wrong. Please try again later."
  const fileError = e.errors.fieldErrors?.file
  return (
    (Array.isArray(fileError) ? fileError[0] : fileError) ||
    e.errors.message ||
    (e.statusCode === 404
      ? "This submission couldn't be found for plagiarism checks."
      : "Something went wrong. Please try again later.")
  )
}

type Props = {
  submissionId: string
  /** Signed link that expires in about an hour: always pass the latest one, never cache it */
  reportUrl: string | null
  onUploaded: () => void
}

const GrammarlyReport = ({ submissionId, reportUrl, onUploaded }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [confirmReplace, setConfirmReplace] = useState(false)

  const { mutate, isPending } = useMutation({
    mutationFn: (file: File) => {
      const data = new FormData()
      data.append("file", file)
      return api.submissions.uploadGrammarlyReport({
        urlParams: { submissionId },
        data,
      })
    },
    onSuccess: () => {
      toast.success("Grammarly report uploaded")
      onUploaded()
    },
    onError: e => toast.error(errorMessage(e)),
  })

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = "" // let the same file be picked again
    if (!file) return

    const isPDF =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    if (!isPDF) return toast.error("The Grammarly report must be a PDF")
    if (file.size > MAX_SIZE)
      return toast.error("The Grammarly report should be at most 25 MB")

    mutate(file)
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-1 rounded-xl border border-onSurface-300 p-2">
      <div className="flex flex-col">
        <Typography variant="title4">Grammarly Report</Typography>
        {!reportUrl && (
          <Typography variant="smallBody" className="text-onSurface-500">
            No report uploaded yet
          </Typography>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {reportUrl && (
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-0.5 text-primary-500"
          >
            <Typography variant="strongSmallBody">Open report</Typography>
            <OpenNewWindow className="size-2" />
          </a>
        )}
        <Button
          size="sm"
          variant="outlined"
          loading={isPending}
          onClick={() =>
            reportUrl ? setConfirmReplace(true) : inputRef.current?.click()
          }
        >
          {reportUrl ? "Replace" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      <Dialog open={confirmReplace} onOpenChange={setConfirmReplace}>
        <DialogContent>
          <DialogBody>
            <Typography variant="title3" className="mb-1">
              Replace Grammarly report?
            </Typography>
            <Typography>
              The current report will be replaced by the new file. This
              can&apos;t be undone.
            </Typography>
            <div className="mt-2 flex justify-end gap-1">
              <Button variant="text" onClick={() => setConfirmReplace(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirmReplace(false)
                  inputRef.current?.click()
                }}
              >
                Choose file
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default GrammarlyReport
